import React from 'react';
import { styled, Theme } from '@mui/material/styles';
import TrimbleMaps, { LngLatBoundsLike } from '@trimblemaps/trimblemaps-js';
import { AllGeoJSON, bbox } from '@turf/turf';

import { NetworkArea } from '../../interfaces/services/areas';
import { MapLayers, MapSources } from '../../utils/maps/enums';
import {
    fitMapToBounds,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    trimbleMapsPopup,
} from '../../utils/maps/mapUtils';
import { addDensityAreasCenterLayer, addDensityAreasHeatmapLayer } from '../../utils/maps/layers/densityAreas';
import { DensityAreasMapSourceProperties, addDensityAreasSource } from '../../utils/maps/sources/densityAreas';
import MapSpinner from '../Loaders/MapSpinner';

import '../../assets/CSS/trimbleMaps.css';

const classesPrefix = 'densityMap';

const classes = {
    map: `${classesPrefix}-map`,
};

const StyledDiv = styled('div')(() => {
    return {
        [`&.${classes.map}`]: {
            height: '100%',
            minHeight: '300px',
        },
    };
});

interface DensityMapProps {
    /** Indicator to show loading spinner while data is fetching. */
    isFetchingData: boolean;
    /** Areas to add to the map */
    densityAreas: NetworkArea[];
    /** MUI theme for styling the map layers */
    theme: Theme;
    /** Indicator to trigger a map resize function. */
    resize?: boolean;
}

interface DensityMapState {
    mapStyleLoaded: boolean;
}

let map: TrimbleMaps.Map | null = null;
const mapContainer = React.createRef<HTMLDivElement>();

class DensityMap extends React.Component<DensityMapProps, DensityMapState> {

    constructor(props: DensityMapProps) {
        super(props);

        TrimbleMaps.APIKey = '173580F5C6F48F44B5006F78909958C1';

        this.state = {
            mapStyleLoaded: false,
        };
    }

    componentDidMount(): void {
        const latitude = 39.828347;
        const longitude = -98.579488;

        if (!mapContainer.current) { return; }

        map = new TrimbleMaps.Map({
            container: mapContainer.current, // Ties to an HTML DOM element
            center: [longitude, latitude], // Sets initial center of map
            style: TrimbleMaps.Common.Style.DATADARK, // Sets initial map style
            minZoom: 1.55, // To stop the zoom from showing multiple worlds
            zoom: 3, // Sets initial zoom level
        }).addControl(
            // Adds the zoom in/out and compass buttons
            new TrimbleMaps.NavigationControl()
        ).addControl(
            new TrimbleMaps.FullscreenControl()
        );

        map.setRegion(TrimbleMaps.Common.Region.WW);

        // README: The event `style.load` is no longer a public event of mapbox. We will need to use some combination of using `styledata` and `map.isStyleLoaded()` at some point.
        // However, these do not work the same and there are several open issues with mapbox that have yet to be resolved, such as this one:
        // https://github.com/mapbox/mapbox-gl-js/issues/9779
        // Once a map style is loaded, add any layers that were on back to the map
        map.on('style.load', (): void => {
            // The style.load event can fire after the component has been unmounted.
            if (map === null) {
                return;
            }

            this.removeDensityAreasEventHandlers();
            this.addDensityAreasToMap();

            // HACK: trigger a resize function to make map take up full width
            map.resize();
            this.setState({ mapStyleLoaded: true });
        });
    }

    componentDidUpdate(prevProps: DensityMapProps): void {
        // if the map is null, hold any CDU updates
        if (map === null) {
            return;
        }

        if (this.state.mapStyleLoaded === false) {
            return;
        }

        // add density areas to map if props have updated
        if (prevProps.densityAreas !== this.props.densityAreas) {
            this.removeDensityAreasEventHandlers();
            this.addDensityAreasToMap();
        }

        // if resize prop is passed and is true force map to resize.
        if (this.props.resize === true) {
            map.resize();
        }
    }

    componentWillUnmount(): void {
        this.removeDensityAreasEventHandlers();
    }

    addDensityAreasToMap = (): void => {
        if (map) {
            // add map source
            const densityAreasSource = addDensityAreasSource(map, this.props.densityAreas || [], this.props.theme);

            const densityAmounts = this.props.densityAreas.map((area) => {
                return area.average_daily_density_amount + 15;
            });

            const minDensityAmount = densityAmounts.length > 0 ? Math.min(...densityAmounts) - 3 : 0;
            const maxDensityAmount = densityAmounts.length > 0 ? Math.max(...densityAmounts) : 1;

            // add area heatmap
            addDensityAreasHeatmapLayer({
                map,
                mapSource: MapSources.densityAreasSource,
                theme: this.props.theme,
                minDensityAmount,
                maxDensityAmount,
                isVisible: true,
            });

            // add area centers
            addDensityAreasCenterLayer({
                map,
                mapSource: MapSources.densityAreasSource,
                minDensityAmount,
                maxDensityAmount,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.densityAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.densityAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });

            const { data } = densityAreasSource;
            if ((data as GeoJSON.FeatureCollection<GeoJSON.Point, DensityAreasMapSourceProperties>).features.length > 0) {
                const boundingBox = bbox(data as AllGeoJSON);
                fitMapToBounds({
                    map,
                    bounds: boundingBox as LngLatBoundsLike,
                    zoomMap: true,
                    maxZoom: 12,
                });
            }
        }
    };

    removeDensityAreasEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.densityAreasCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.densityAreasCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.densityAreasCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            }
        }
    };

    render(): React.JSX.Element {
        return (
            <StyledDiv
                id='trimble-map-container'
                ref={mapContainer}
                className={classes.map}
                data-qa='densityMap-container'
            >
                {
                    this.props.isFetchingData &&
                    <MapSpinner />
                }
            </StyledDiv>
        );
    }

}

export default DensityMap;
